import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Title, Text, Card, Badge, Group, Button,
  ActionIcon, Tooltip, Progress, Box,
} from '@mantine/core';
import {
  IconSearch, IconEye, IconRefresh, IconArrowLeft,
} from '@tabler/icons-react';
import { Flex, TextInput, CCMSDataTable, ListPagination } from '@/components';
import { EmptyState } from '@components/ccms-components/utils/EmptyState';
import type { TableColumn } from '@/components/ccms-components/data-display/types';
import type { SortConfig } from '@/components/ccms-components/types/common';
import { sortRows } from '@/utils/tableSort';
import {
  useFacilitatorPortalService,
  FacilitatorRegistrationView,
} from '@actschurch/ccms-sdk';
import { useSidebarCountsContext } from '@/contexts/SidebarCountsContext';
import FacilitatorPortalNotice from './FacilitatorPortalNotice';

const PAGE_SIZE = 10;

interface EnrolmentsListViewProps {
  fixedStatusID?: number;
  title?: string;
  subtitle?: string;
  /**
   * Narrows the list to one course. Matched on **name**, because
   * The course to narrow to, by **id** (SDK 0.0.50). Used by the `/Grow/Enrolment` drill-down.
   * ⚠️ Was `courseName` — matching on the name merged two courses that happened to share one.
   */
  courseID?: number;
  /** When given, renders a Back control in the header (the course drill-down uses this). */
  onBack?: () => void;
}

export function EnrolmentsListView({
  fixedStatusID,
  title: titleProp,
  subtitle,
  courseID,
  onBack,
}: EnrolmentsListViewProps) {
  const navigate = useNavigate();
  const portalService = useFacilitatorPortalService();
  const { refetch } = useSidebarCountsContext();

  const [registrations, setRegistrations] = useState<FacilitatorRegistrationView[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [activePage, setActivePage] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState(
    fixedStatusID !== undefined ? String(fixedStatusID) : '',
  );

  useEffect(() => {
    if (fixedStatusID !== undefined) setFilterStatus(String(fixedStatusID));
  }, [fixedStatusID]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await portalService.myRegistrations();
      setRegistrations(res ?? []);
      refetch();
    } catch (e) {
      // Surfaced inline (see FacilitatorPortalNotice) rather than a toast. A 403 here means the
      // account has no facilitator record — a setup problem the user needs to read and act on,
      // not a transient blip that a disappearing toast leaves as an empty table.
      console.error('Error fetching enrolments:', e);
      setLoadError(e);
      setRegistrations([]);
    } finally {
      setLoading(false);
    }
  }, [portalService, refetch]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    const search = searchTerm.toLowerCase();
    return registrations.filter((r) => {
      const matchesSearch = !search ||
        String(r.registrationID).includes(search) ||
        (r.reference ?? '').toLowerCase().includes(search) ||
        (r.courseName ?? '').toLowerCase().includes(search);
      const matchesStatus = !filterStatus || String(r.statusID) === filterStatus;
      const matchesCourse = courseID === undefined || r.courseID === courseID;
      return matchesSearch && matchesStatus && matchesCourse;
    });
  }, [filterStatus, registrations, searchTerm, courseID]);


  /**
   * On the course drill-down the heading is the course's own name.
   *
   * ⚠️ Taken from the rows rather than the URL: the `course` param is an **id** now, so the name has
   * to come from the data. Falls back to the caller's title while the rows are still loading.
   */
  const title = useMemo(() => {
    if (titleProp) return titleProp;
    if (courseID === undefined) return titleProp;
    return registrations.find((r) => r.courseID === courseID)?.courseName?.trim() || 'Course';
  }, [titleProp, courseID, registrations]);

  /**
   * Noun for the empty states. Derived from the page title so the status-scoped pages read
   * naturally ("No active enrolments yet") instead of a generic "No enrolments yet".
   */
  const emptyEntityLabel = (title ?? 'enrolments').toLowerCase().includes('enrolment')
    ? (title ?? 'enrolments').toLowerCase()
    : 'enrolments';

     /**
   * "NameA", "NameA & NameB", or "NameA, NameB & NameC" — mirrors how the count column used to
   * read, but with the people themselves instead of a bare number. Falls back to the count (or an
   * em dash) when the API hasn't sent names for some reason, so the column never goes blank.
   */
  const formatParticipants = (row: FacilitatorRegistrationView): string => {
    const names = (row.participantNames ?? []).filter(Boolean);
    if (names.length === 0) return row.participantCount ? String(row.participantCount) : '—';
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} & ${names[1]}`;
    return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
  };

  const columns: TableColumn<FacilitatorRegistrationView>[] = [
    {
      key: 'registrationID',
      header: 'ID',
      accessor: (r) => r.registrationID ?? r.reference,
      sortable: true,
      width: 50,
    },
    {
      key: 'description',
      header: 'Description',
      accessor: (r) => formatParticipants(r),
      sortable: true,
    },
    {
      key: 'periodDisplay',
      header: 'Period',
      accessor: (r) => r.periodDisplay ?? '—',
      sortable: true,
    },
        {
      key: 'courseName',
      header: 'Course',
      accessor: (r) => r.courseName ?? 'Course',
      sortable: true,
      render: (_, row) => (
        <Badge
          variant="filled"
          style={
            row.courseColor
              ? { backgroundColor: `${row.courseColor}22`, color: row.courseColor }
              : undefined
          }
          color={row.courseColor ? undefined : 'gray'}
        >
          {row.courseName ?? '—'}
        </Badge>
      ),
    },
    {
      key: 'locationName',
      header: 'Location',
      accessor: (r) => (r.isRemote ? 'Remote' : r.locationName ?? '—'),
      sortable: true,
    },
    {
      key: 'statusID',
      header: 'Status',
      accessor: (r) => r.statusName ?? r.statusID,
      sortable: true,
      align: 'center',
      render: (_, row) => (
        <Badge
          variant="light"
          style={
            row.statusColor
              ? { backgroundColor: `${row.statusColor}22`, color: row.statusColor }
              : undefined
          }
          color={row.statusColor ? undefined : 'gray'}
        >
          {row.statusName ?? `Status ${row.statusID}`}
        </Badge>
      ),
    },
    {
      key: 'isLead',
      header: 'Role',
      accessor: (r) => (r.isLead ? 'Lead' : 'Facilitator'),
      sortable: true,
      align: 'center',
      render: (_, row) => (
        <Badge size="sm" variant="light" color={row.isLead ? 'blue' : 'gray'}>
          {row.isLead ? 'Lead' : 'Facilitator'}
        </Badge>
      ),
    },
    {
      key: 'participantCount',
      header: 'Participants',
      accessor: (r) => r.participantCount ?? 0,
      sortable: true,
      align: 'center',
    },
    // {
    //   key: 'progressPercent',
    //   header: 'Progress',
    //   accessor: (r) => r.progressPercent ?? 0,
    //   sortable: true,
    //   render: (_, row) => (
    //     <Group gap={6} wrap="nowrap" style={{ minWidth: 100 }}>
    //       <Progress value={row.progressPercent ?? 0} size="sm" style={{ flex: 1 }} />
    //       <Text size="xs" c="dimmed">{row.progressPercent ?? 0}%</Text>
    //     </Group>
    //   ),
    // },
    {
      key: 'nextSessionDisplay',
      header: 'Next Session',
      accessor: (r) => r.nextSessionDisplay ?? '—',
      sortable: true,
    },
    {
      key: 'actions',
      header: 'Actions',
      accessor: (r) => r,
      sortable: false,
      align: 'center',
      render: (_, row) => (
        <Group gap={6} justify="center" wrap="nowrap">
          <Tooltip label="View">
            <ActionIcon
              variant="light"
              color="blue"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/Grow/Enrolment/RegistrationDetails/${row.registrationID}`);
              }}
            >
              <IconEye size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
      ),
    },
  ];

  /**
   * ⚠️ Sorting lives here, not in `CCMSDataTable`. The table only draws the arrow and calls `onSort`;
   * this page previously threw that away, so the arrows moved and the rows never did.
   * `sortRows` keys off each column's own `accessor`, so it cannot disagree with the cell.
   *
   * Declared **after** `columns` on purpose: `useMemo` runs during render, so referencing `columns`
   * from above it would hit the temporal dead zone.
   */
  const sorted = useMemo(
    () => sortRows(filtered, columns, sortConfig),
    [filtered, columns, sortConfig],
  );

  const paginated = sorted.slice((activePage - 1) * pageSize, activePage * pageSize);

  return (
    <Container size="xl" py="xl">
      {onBack && (
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onBack} mb="xs">
          All courses
        </Button>
      )}

      <Group justify="space-between" align="center" mb="md">
        <div>
          <Title order={2}>{title ?? 'Enrolment'}</Title>
          <Text c="dimmed" size="sm">{subtitle ?? "Registrations you're facilitating."}</Text>
        </div>
      </Group>

      {loadError ? (
        <FacilitatorPortalNotice error={loadError} subject="your enrolments" onRetry={fetchAll} />
      ) : (
        <>
          <Card withBorder radius="md" padding="lg" mb="md">
            <Flex gap="sm" align="center" justify="space-between" wrap="wrap">
              <TextInput
                placeholder="Search by course, reference, ID…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.currentTarget.value)}
                leftSection={<IconSearch size={16} />}
                radius="md"
                styles={{ input: { width: '300px' } }}
                size="sm"
              />
              <Group gap="sm">
                <Tooltip label="Refresh">
                  <ActionIcon variant="subtle" onClick={fetchAll} loading={loading} size="lg">
                    <IconRefresh size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Flex>
          </Card>

          <Card withBorder radius="md" padding="lg">
            {/* Same empty treatment as Pending Enrolments: one plain "nothing here yet" message,
                using the component's default copy. These are status-scoped views rather than
                filtered ones, so there is no clear-filters variant to offer. */}
            {!loading && filtered.length === 0 ? (
              <EmptyState variant="no-data" entityLabel={emptyEntityLabel} />
            ) : (
              <>
                <CCMSDataTable
                  data={paginated}
                  columns={columns}
                  loading={loading}
                  onSort={(config) => { setSortConfig(config); setActivePage(1); }}
                  striped
                  highlightOnHover
                />
                <ListPagination
                  page={activePage}
                  pageSize={pageSize}
                  totalItems={filtered.length}
                  onPageChange={setActivePage}
                  onPageSizeChange={(size) => { setPageSize(size); setActivePage(1); }}
                  showPageSize
                />
              </>
            )}
          </Card>
        </>
      )}
    </Container>
  );
}
